import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router";
import {
	Alert,
	AlertContainer,
	AlertContent,
	AlertDescription,
	AlertTitle,
} from "@/components/ui/alert";
import { Cake, Check, ClockAlert, Copy, MoveRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useApp } from "@/app/providers/app-provider";

interface Discount {
	code: string;
	title: string;
	icon: React.ReactNode;
	text: React.ReactNode;
	expires: string; // YYYY-MM-DD
	starts: string; // YYYY-MM-DD
}

const discounts: Discount[] = [
	{
		code: "HAPPY-12TH-BDAY",
		title: "Let's Party!",
		icon: <Cake size={24} className="block ml-2 mr-4 shrink-0" />,
		text: (
			<span>
				It's our birthday! Enter this promotion code below to get an additional{" "}
				<strong className="bolder underline">12% off</strong>.
			</span>
		),
		expires: "2025-03-31",
		starts: "2025-03-01",
	},
	{
		code: "MEMORIAL2025",
		title: "Turn up the focus!",
		icon: <Star size={24} className="hidden md:block ml-2 mr-4 shrink-0" />,
		text: (
			<span>
				And turn down the distractions this Memorial Day. Use the code below to
				take an additional <strong className="bolder underline">25% off</strong>{" "}
				our Premium services.
			</span>
		),
		expires: "2025-05-28",
		starts: "2025-05-21",
	},
	{
		code: "FATHERSDAY25",
		title: "Honoring All of the Dad's",
		icon: <Star size={24} className="hidden md:block ml-2 mr-4 shrink-0" />,
		text: (
			<span>
				For the dads who do it all—stay in the zone this Father's Day. Take an
				additional <strong className="bolder underline">20% off</strong> our
				Premium sounds.
			</span>
		),
		expires: "2025-06-18",
		starts: "2025-06-11",
	},
	{
		code: "JULY4TH2025",
		title: "Freedom from Distractions",
		icon: <Cake size={24} className="hidden md:block ml-2 mr-4 shrink-0" />,
		text: (
			<span>
				4TH of July FLASH SALE! 50% off Coffitivity Premium - One day only!
			</span>
		),
		expires: "2025-07-04",
		starts: "2025-07-04",
	},
];

const SpecialDiscountAlert = ({ discount }: { discount: Discount }) => {
	const [copied, setCopied] = useState(false);

	const copyCode = async () => {
		try {
			await navigator.clipboard.writeText(discount.code);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy code:", err);
		}
	};

	const endDateParts = discount.expires.split("-").map(Number);
	const endDate = new Date(
		endDateParts[0],
		endDateParts[1] - 1, // Month is 0-indexed
		endDateParts[2],
		23, // Set to the end of the day
		59,
		59,
		999, // Include entire end day
	);
	const displayEndDate = endDate.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric",
	});

	return (
		<Alert
			variant="primary"
			className="rounded-none bg-primary md:bg-background md:rounded-lg py-2 px-4 sm:px-8 md:p-4"
		>
			<AlertContainer>
				{discount.icon}
				<AlertContent className="text-sm">
					<Link to="/pricing">
						<AlertTitle className="mr-2 md: ml-0 text-base md:text-md inline md-block text-primary-foreground md:text-primary">
							{discount.title}
						</AlertTitle>
					</Link>
					<div className="flex flex-col gap-1 text-primary-foreground md:text-foreground leading-4">
						<Link to="/pricing">
							<p className="">
								{discount.text} Expires {displayEndDate}.
							</p>
						</Link>
						<p className="flex items-center gap-2">
							Code: <span className="font-bold">{discount.code} </span>
							<Button
								variant="secondary"
								size="sm"
								onClick={copyCode}
								className="h-6 w-6 p-0 hover:bg-gray-200 dark:hover:bg-gray-700"
							>
								{copied ? (
									<Check className="h-3 w-3 text-primary" />
								) : (
									<Copy className="h-3 w-3" />
								)}
							</Button>
						</p>
					</div>
				</AlertContent>
			</AlertContainer>
			<Link to="/pricing">
				<MoveRight size={24} className="shrink-0 mx-2" />
			</Link>
		</Alert>
	);
};

const StandardPremiumAlert = () => {
	return (
		<Link to="/pricing">
			<Alert
				variant="primary"
				className="rounded-none bg-primary md:bg-background md:rounded-lg py-2 px-4 sm:px-8 md:p-4 leading-3"
			>
				<AlertContainer>
					<ClockAlert
						size={24}
						className="hidden md:block ml-2 mr-4 shrink-0"
					/>
					<div>
						<AlertTitle className="mr-2 md: ml-0 text-sm md:text-lg inline md-block text-primary-foreground md:text-primary">
							Heads up!
						</AlertTitle>
						<AlertDescription className="inline md:block text-sm md:text-base text-primary-foreground md:text-foreground">
							You're missing out on our full experience. Get{" "}
							<span className="md:font-bold">40% off</span> with our annual
							plan. <span className="underline">Cancel anytime</span>.
						</AlertDescription>
					</div>
				</AlertContainer>
				<MoveRight size={24} className="shrink-0 mx-2" />
			</Alert>
		</Link>
	);
};

const GetPremiumAlert = ({ className }: { className?: string }) => {
	// Check through the discounts to see if any are active
	const activeDiscount = discounts.find((discount) => {
		const currentDate = new Date(); // This uses the client’s local time
		const startDateParts = discount.starts.split("-").map(Number);
		const endDateParts = discount.expires.split("-").map(Number);

		// Create start and end dates using local time
		const startDate = new Date(
			startDateParts[0],
			startDateParts[1] - 1,
			startDateParts[2],
		);
		const endDate = new Date(
			endDateParts[0],
			endDateParts[1] - 1,
			endDateParts[2],
			23,
			59,
			59,
			999,
		); // Include entire end day

		return currentDate >= startDate && currentDate <= endDate;
	});

	const location = useLocation();
	const { appStore } = useApp();
	const style = appStore.subscription ? "hidden" : "w-full mb-4 md:mb-10";

	const hiddenPages = [
		"/pricing",
		"/checkout",
		"/terms",
		"/contact",
		"/account",
	];
	const hiddenPageStyle = hiddenPages.includes(location.pathname)
		? "hidden"
		: "";

	return (
		<section className={cn(style, hiddenPageStyle, className)}>
			{activeDiscount ? (
				<SpecialDiscountAlert discount={activeDiscount} />
			) : (
				<StandardPremiumAlert />
			)}
		</section>
	);
};

export default GetPremiumAlert;
